import type { BeforeAfterRow } from '../types/content'

export const pathSteps = ['Assess', 'Build', 'Control', 'Apply', 'Link', 'Decide', 'Independence']

export const coachSteps = [
  'Assess the rider.',
  'Identify the limiter.',
  'Choose the skill.',
  'Run the drill.',
  'Correct the mistake.',
  'Progress the rider.',
  'Know what comes next.',
]

export const notStatements = [
  'This is not a random collection of MTB exercises.',
  'This is not a theoretical cycling ebook that leaves you figuring out how to turn concepts into actual coaching sessions.',
  'This is not a guide written for recreational riders that you have to completely adapt before it becomes useful.',
]

export const progressionStages = [
  'Basic rider control',
  'Bike handling',
  'Braking and cornering',
  'Climbing and descending',
  'Roots, rocks and technical terrain',
  'Flow and trail reading',
  'Advanced rider decisions',
]

export const isolatedResources = [
  'One guide for braking.',
  'Another for cornering.',
  'Another for technical terrain.',
  'Another list of drills.',
]

export const skillChain = [
  'A weakness in balance affects technical control.',
  'Poor braking affects cornering.',
  'Poor vision affects line choice.',
  'Poor line choice affects technical terrain.',
  'Poor trail reading affects flow.',
]

export const knowItems = [
  'What to teach',
  'How to teach it',
  'What to watch',
  'What to say',
  'What to correct',
  'How to regress it',
  'How to progress it',
  'How to know it worked',
  'What to teach next',
]

export const bonusTableRows = [
  '150 MTB Drills Library',
  'MTB Coaching Cues & Error Correction Handbook',
  'MTB Rider Assessment & Skill Level System',
  '50 MTB Skills Games & Coaching Challenges',
  '30 MTB Clinics & Ready-to-Run Backup Sessions',
]

export const offerVolumes = [
  'Volume 1 — MTB Foundations, Rider Assessment & Coaching Methodology',
  'Volume 2 — Bike Handling, Balance & Technical Control',
  'Volume 3 — Cornering, Braking & Line Choice Systems',
  'Volume 4 — Climbing, Descending & Technical Terrain Development',
  'Volume 5 — Speed, Flow, Trail Reading & Advanced Riding Decisions',
  'Volume 6 — 150 Ready-to-Coach MTB Sessions & Rider Development System',
]

export const offerBonuses = [
  'Bonus 1 — 150 MTB Drills Library',
  'Bonus 2 — MTB Coaching Cues & Error Correction Handbook',
  'Bonus 3 — MTB Rider Assessment & Skill Level System',
  'Bonus 4 — 50 MTB Skills Games & Coaching Challenges',
  'Bonus 5 — 30 MTB Clinics & Ready-to-Run Backup Sessions',
]

export const offerExtras = [
  'Immediate digital access',
  'Professional PDF format',
  'Foundation to Advanced progression',
  'Visual coaching diagrams',
  'Ready-to-use coaching material',
  '7-Day Money-Back Guarantee',
]

export const beforeAfterRows: BeforeAfterRow[] = [
  { before: '“What am I going to teach tomorrow?”', after: '“What does this rider need next?”' },
  { before: 'Searching for drills.', after: '150 MTB drills ready.' },
  { before: 'Building sessions from zero.', after: '150 ready-to-coach sessions.' },
  {
    before: 'Seeing the mistake but searching for the correction.',
    after: '100+ coaching cues and correction strategies.',
  },
  {
    before: 'Wondering if the rider is ready to progress.',
    after: 'A complete Rider Assessment & Skill Level System.',
  },
  { before: 'Running out of ideas.', after: '50 skills games + 30 clinics and backup sessions.' },
]

export const audience = [
  'MTB coaches',
  'Mountain bike instructors',
  'Skills coaches',
  'Private coaches',
  'Small-group coaches',
  'Clinic instructors',
  'Junior coaches',
  'Adult rider coaches',
  'Coaches working from beginner to advanced levels',
]

export const useCases = [
  'Private sessions',
  'Group coaching',
  'Clinics',
  'Skill-development programs',
  'Multi-week rider progressions',
  'Trail coaching',
  'Technical coaching sessions',
]

export const guaranteeSteps = [
  'Open the complete system.',
  'Explore the six volumes.',
  'Review the 150 sessions.',
  'Use the drills.',
  'Try the coaching cues.',
  'Assess one of your riders.',
  'Take a ready-to-coach session into your next coaching day.',
]

export const keepDoing = [
  'Opening a blank document.',
  'Searching for another drill.',
  'Reusing another familiar exercise.',
  'Trying to remember what you taught last week.',
  'Building another session from scratch.',
  'Wondering what the rider should do next.',
  'And doing it all again next week.',
]

export const openSteps = [
  'Assess the rider.',
  'Identify the limiter.',
  'Choose the skill.',
  'Open the session.',
  'Run the drill.',
  'Give the cue.',
  'Correct the mistake.',
  'Regress when necessary.',
  'Progress when ready.',
  'And know exactly what comes next.',
]

export const noMore = [
  'Hours searching for MTB session ideas',
  'Sessions that feel improvised',
  'Repeating the same drills every week',
  'Riders progressing without structure',
  'Guessing how to correct common errors',
  'Wondering what to teach next',
  'Losing evenings building tomorrow’s session',
]

export const stillMatters = [
  'Your experience still matters.',
  'Your observation still matters.',
  'Your communication still matters.',
  'Your judgment still matters.',
]

export const everythingIncluded = [
  '6 Complete MTB Coaching Volumes',
  '150 Ready-to-Coach MTB Sessions',
  '150 Additional MTB Drills',
  '100+ Coaching Cues & Correction Strategies',
  'Complete Rider Assessment & Skill Level System',
  '50 MTB Skills Games & Coaching Challenges',
  '30 MTB Clinics & Ready-to-Run Backup Sessions',
  'Foundation to Advanced Rider Development',
  'Visual Coaching Systems',
  'Technical Progressions',
  'Regressions & Corrections',
  'Finished PDF Format',
  'Immediate Access',
  '7-Day Money-Back Guarantee',
]

export const finalTrust = [
  'Secure Payment',
  'Immediate Access',
  'Professional PDF Format',
  '7-Day Money-Back Guarantee',
]
